/**
 * Authentication Error Handler Service
 *
 * Service to handle different authentication error types.
 * Dispatches errors to appropriate handlers and returns modal actions.
 */

import { createLogger } from '@utils/Logger'
import { StepName } from '@account/components/Register/types'
import { AUTH_ERROR_TYPES, isMfaEnrollmentError, isMfaVerificationError } from '@constants'
import { prepareMfaEnrollmentData, prepareMfaVerificationData } from './mfaDataService'

const logger = createLogger('AuthErrorHandlerService')

/**
 * Modal action result from error handling
 */
export interface ErrorHandlerResult {
  /** Modal action to take */
  action: 'openSignIn' | 'openRegister' | 'none'
  /** Modal ID to use */
  modalId: string
  /** Step name for register modal */
  stepName?: StepName
  /** Error message for sign in modal */
  errorMessage?: string
  /** Whether the error was handled successfully */
  success: boolean
}

/**
 * Parameters for handling authentication errors
 */
export interface HandleAuthErrorParams {
  errorType: string
  modalId: string
  stateToken?: string
  errorMessage?: string
}

/**
 * Main dispatcher for authentication errors
 *
 * @param params - Error handling parameters
 * @returns Error handler result
 */
const handleAuthErrorImpl = (
  params: HandleAuthErrorParams,
  handlers: {
    mfaEnroll: typeof handleMfaEnrollRequired
    mfaVerify: typeof handleMfaVerificationRequired
    generic: typeof handleGenericAuthError
  },
): ErrorHandlerResult => {
  const { errorType, modalId, stateToken, errorMessage } = params

  // Handle Okta account locked - route to MFA critical lock screen when in an MFA flow
  if (errorType === AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED) {
    if (stateToken) {
      return {
        action: 'openRegister',
        modalId,
        stepName: StepName.MFA_CRITICAL_LOCK_SCREEN,
        success: true,
      }
    }

    // Without stateToken, default to sign-in modal and let SignIn handle lock step
    return {
      action: 'openSignIn',
      modalId,
      errorMessage: AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED,
      success: true,
    }
  }

  // Handle MFA enrollment required
  if (isMfaEnrollmentError(errorType)) {
    return handlers.mfaEnroll(modalId, stateToken)
  }

  // Handle MFA verification required
  if (isMfaVerificationError(errorType)) {
    return handlers.mfaVerify(modalId, stateToken)
  }

  // Handle generic authentication errors
  return handlers.generic(modalId, errorMessage || errorType)
}

/**
 * Handle MFA enrollment required error
 *
 * @param modalId - Modal ID to use
 * @param stateToken - Optional state token
 * @returns Error handler result
 */
export const handleMfaEnrollRequired = (modalId: string, stateToken?: string): ErrorHandlerResult => {
  try {
    logger.debug('Handling MFA enrollment required', { modalId, hasStateToken: !!stateToken })

    // Prepare MFA enrollment data
    const success = prepareMfaEnrollmentData(stateToken)

    if (!success) {
      logger.error('Failed to prepare MFA enrollment data')
      return {
        action: 'openSignIn',
        modalId,
        errorMessage: AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED,
        success: false,
      }
    }

    return {
      action: 'openRegister',
      modalId,
      stepName: StepName.MFA_SETUP_SELECTOR,
      success: true,
    }
  } catch (error) {
    logger.error('Error handling MFA enrollment required:', error)
    return {
      action: 'openSignIn',
      modalId,
      errorMessage: AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED,
      success: false,
    }
  }
}

/**
 * Handle MFA verification required error
 *
 * @param modalId - Modal ID to use
 * @param stateToken - Optional state token
 * @returns Error handler result
 */
export const handleMfaVerificationRequired = (modalId: string, stateToken?: string): ErrorHandlerResult => {
  try {

    // Prepare MFA verification data
    const success = prepareMfaVerificationData(stateToken)

    if (!success) {
      logger.error('Failed to prepare MFA verification data')
      return {
        action: 'openSignIn',
        modalId,
        errorMessage: AUTH_ERROR_TYPES.MFA_VERIFICATION_REQUIRED,
        success: false,
      }
    }

    return {
      action: 'openRegister',
      modalId,
      stepName: StepName.MFA_VERIFY_CODE,
      success: true,
    }
  } catch (error) {
    logger.error('Error handling MFA verification required:', error)
    return {
      action: 'openSignIn',
      modalId,
      errorMessage: AUTH_ERROR_TYPES.MFA_VERIFICATION_REQUIRED,
      success: false,
    }
  }
}

/**
 * Handle generic authentication errors
 *
 * @param modalId - Modal ID to use
 * @param errorMessage - Error message to display
 * @returns Error handler result
 */
export const handleGenericAuthError = (modalId: string, errorMessage: string): ErrorHandlerResult => {
  logger.debug('Handling generic authentication error', { modalId, errorMessage })

  return {
    action: 'openSignIn',
    modalId,
    errorMessage,
    success: true,
  }
}

export const handleAuthError = (params: HandleAuthErrorParams): ErrorHandlerResult =>
  handleAuthErrorImpl(params, {
    mfaEnroll: handleMfaEnrollRequired,
    mfaVerify: handleMfaVerificationRequired,
    generic: handleGenericAuthError,
  })

/**
 * Handle URL-based authentication errors
 *
 * @param errorMessage - Decoded error message from URL
 * @param modalId - Modal ID to use
 * @param stateToken - Optional state token from URL
 * @returns Error handler result
 */
export const handleUrlAuthError = (errorMessage: string, modalId: string, stateToken?: string): ErrorHandlerResult => {
  logger.debug('Handling URL authentication error', { errorMessage, modalId, hasStateToken: !!stateToken })

  return handleAuthError({
    errorType: errorMessage,
    modalId,
    stateToken,
    errorMessage,
  })
}

/**
 * Handle event-based authentication errors
 *
 * @param errorMessage - Error message from event
 * @param modalId - Modal ID to use
 * @param stateToken - Optional state token from event
 * @returns Error handler result
 */
export const handleEventAuthError = (errorMessage: string, modalId: string, stateToken?: string): ErrorHandlerResult => {

  return handleAuthError({
    errorType: errorMessage,
    modalId,
    stateToken,
    errorMessage,
  })
}
